import { Injectable } from '@angular/core';
import { LoadingState } from '@lcs/shared/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { getCurrentUserId } from 'src/app/authentication/state/authentication.selector';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { tileSave } from 'src/app/pages/tiles/state/tile.actions';
import {
   compactLayout,
   getPreservedLayoutFromDashboard,
   mapLayoutToTargetColumns,
} from '../../models/dashboard-layout-utils';
import { Tile } from '../../models/tile.model';
import { getCurrentDashboardColumnLayoutSize, getSelectedDashboard } from '../../state/dashboard.selectors';
import { TileSet } from '../../tile-set-type-select/models/tile-set.model';
import { GetTileSets_tileSetsConnection_items } from '../queries/generated/GetTileSets';
import { AddTileApiService } from '../services/add-tile-api.service';
import * as AddDashboardTileActions from './add-dashboard-tile.actions';
import { selectCallState } from './add-dashboard-tile.selectors';

@Injectable()
export class AddDashboardTileEffects {
   pageEnter$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(AddDashboardTileActions.pageEnter),
         withLatestFrom(this.store.pipe(select(selectCallState))),
         filter(([_, callState]) => callState === LoadingState.Init),
         switchMap(() => {
            return of(AddDashboardTileActions.loadTileSets());
         })
      );
   });

   loadTileSets$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(AddDashboardTileActions.loadTileSets),
         switchMap(() =>
            this.addTileApiService.loadTileSets().pipe(
               map((response) =>
                  AddDashboardTileActions.loadTileSetsSuccess({
                     tileSets: this.mapToTileSet(response.data.tileSetsConnection.items),
                  })
               ),
               catchError((error) => of(AddDashboardTileActions.loadTileSetsFailure(error)))
            )
         )
      );
   });

   addTile = createEffect(() => {
      return this.actions$.pipe(
         ofType(AddDashboardTileActions.addTile),
         withLatestFrom(
            this.store.pipe(select(getCurrentUserId)),
            this.store.pipe(select(getSelectedDashboard)),
            this.store.pipe(select(getCurrentDashboardColumnLayoutSize))
         ),
         switchMap(([action, currentUserId, selectedDashboard, currentDashboardColumnLayoutSize]) => {
            const { previewTileConfig, params } = action;
            if (currentUserId === null || selectedDashboard === null || !previewTileConfig?.tile.tileId) return EMPTY;

            const layout = getPreservedLayoutFromDashboard(selectedDashboard);
            const compactedLayout = compactLayout(layout, 'vertical', currentDashboardColumnLayoutSize);

            const newDisplayLayout = mapLayoutToTargetColumns(
               selectedDashboard?.lastSavedColumnLayoutSize ?? 1,
               currentDashboardColumnLayoutSize,
               compactedLayout
            );

            const targetWidth = previewTileConfig?.dashboardTileLayoutConfiguration.width ?? 1;
            const targetHeight = previewTileConfig?.dashboardTileLayoutConfiguration.height ?? 1;
            const cols = currentDashboardColumnLayoutSize ?? 6;
            const openSpace = this.addTileApiService.getOpenSpace(newDisplayLayout, targetWidth, targetHeight, cols);

            const dashboardTileId = 0;
            const tileId = previewTileConfig.tile.tileId;
            const parameters = {
               ...params,
               tileLayoutConfiguration: {
                  ...params.tileLayoutConfiguration,
                  canEditRefreshTime: true,
                  height: targetHeight,
                  width: targetWidth,
                  column: openSpace.x,
                  row: openSpace.y,
                  tileSetType: {
                     tile: {
                        tileId,
                     },
                  },
               },
            };

            return of(tileSave({ dashboardTileId, parameters, tileId }));
         })
      );
   });

   addTileSuccess$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(AddDashboardTileActions.addTileSuccess),
            tap((action) => {
               this.dialogService.close();
            })
         );
      },
      { dispatch: false }
   );

   constructor(
      private store: Store,
      private actions$: Actions,
      private addTileApiService: AddTileApiService,
      private dialogService: LcsDialogService
   ) {}

   private mapToTileSet(data: GetTileSets_tileSetsConnection_items[]): TileSet[] {
      return data
         .map((d) => {
            return {
               tileId: null,
               tileSetId: d.tileSetId,
               name: d.name ?? '',
               description: d.description ?? '',
               designerDescription: d.designerDescription ?? '',
               tileSetTypes:
                  d.tileSetTypeConnection.items.map((tstc) => {
                     return {
                        tileSetTypeId: tstc.tileSetTypeId,
                        tile: tstc.tile as Tile,
                     };
                  }) ?? [],
            };
         })
         .sort((a, b) => (a.description > b.description ? 1 : -1));
   }
}
